import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createFollowUp, getFollowUps, deleteFollowUp, updateFollowUpStatus } from '../store/slices/followUpSlice';
import { X, Plus, Trash2, Calendar, User, Clock, Printer } from 'lucide-react';
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext';
import printJS from 'print-js';


const FollowUpModal = ({ isOpen, onClose, plotId, bookingId, customerId }) => {
    const { theme } = useSidebarContext();
    const dispatch = useDispatch();
    const { followUps, loading } = useSelector((state) => state.followUp);
    const authState = useSelector((state) => state.auth);
    const { user, activeUser, users } = authState;
    const currentUser = user || activeUser;

    // Sort follow-ups by date desc
    const sortedFollowUps = [...followUps].sort((a, b) => new Date(b.date) - new Date(a.date));

    console.log("FollowUpModal Auth Debug:", {
        fullState: authState,
        user,
        activeUser,
        currentUser,
        currentUserId: currentUser?._id
    });

    const [remarks, setRemarks] = useState('');
    const [nextDate, setNextDate] = useState('');
    const [status, setStatus] = useState('Pending');

    useEffect(() => {
        if (isOpen && plotId) {
            dispatch(getFollowUps(plotId));
        }
    }, [isOpen, plotId, dispatch]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!remarks.trim()) return;

        const followUpData = {
            plot_id: plotId,
            booking_id: bookingId,
            customer_id: customerId,
            created_by: currentUser?.id || currentUser?._id,
            remarks,
            next_followup_date: nextDate || null,
            status: status || 'Pending'
        };

        if (!followUpData.created_by) {
            alert("Error: User not identified. Please refresh the page.");
            return;
        }

        await dispatch(createFollowUp(followUpData));
        setRemarks('');
        setNextDate('');
        setStatus('Pending');
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this follow-up?')) {
            dispatch(deleteFollowUp(id));
        }
    };

    const handlePrintFollowUp = (item) => {
        const customer = users?.find(u => u._id === customerId);
        const customerData = customer?.customerData || customer;
        const contactInfo = customerData?.contactInfo;

        const customerName = customerData?.name || 'Unknown Customer';
        const contactDetails = contactInfo?.phone || contactInfo?.email || 'N/A';

        // ✅ Fixed: resolve admin name checking username or name -> lookup in users array -> currentUser
        const adminName = item.created_by?.username
            || item.created_by?.name
            || users?.find(u => u._id === (typeof item.created_by === 'object' ? item.created_by?._id : item.created_by))?.username
            || currentUser?.username
            || currentUser?.name
            || 'Unknown';

        const followupDate = new Date(item.date).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
        const nextDate = item.next_followup_date ? new Date(item.next_followup_date).toLocaleDateString() : 'Not scheduled';
        const statusLabel = item.status || 'Pending';

        let statusStyle = '';
        if (statusLabel === 'Completed') {
            statusStyle = 'background:#dcfce7; color:#15803d; border:1.5px solid #86efac;';
        } else if (statusLabel === 'Cancelled') {
            statusStyle = 'background:#fee2e2; color:#dc2626; border:1.5px solid #fca5a5;';
        } else {
            statusStyle = 'background:#fef9c3; color:#a16207; border:1.5px solid #fde047;';
        }

        const initials = adminName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
        const avatarColors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];
        const avatarBg = avatarColors[adminName.charCodeAt(0) % avatarColors.length];

        const printContent = `
    <div style="font-family:'Plus Jakarta Sans',sans-serif; background:#f1f5f9; color:#0f172a; margin:0; padding:0;">

        <!-- TOP RAINBOW BAR -->
        <div style="height:6px; background:linear-gradient(90deg,#6366f1 0%,#8b5cf6 30%,#ec4899 60%,#f59e0b 100%);"></div>

        <!-- DARK HEADER -->
        <div style="background:#0f172a; padding:48px 56px 44px; position:relative; overflow:hidden;">
            <div style="position:absolute;top:-70px;right:-70px;width:260px;height:260px;border-radius:50%;background:radial-gradient(circle,rgba(99,102,241,0.22) 0%,transparent 70%);"></div>
            <div style="position:absolute;bottom:-50px;left:160px;width:180px;height:180px;border-radius:50%;background:radial-gradient(circle,rgba(236,72,153,0.16) 0%,transparent 70%);"></div>
            <div style="position:absolute;top:20px;right:260px;width:100px;height:100px;border-radius:50%;background:radial-gradient(circle,rgba(251,191,36,0.13) 0%,transparent 70%);"></div>

            <div style="position:relative;z-index:2;display:flex;justify-content:space-between;align-items:center;">
                <div>
                    <div style="display:inline-flex;align-items:center;gap:8px;background:rgba(99,102,241,0.18);border:1px solid rgba(99,102,241,0.38);border-radius:100px;padding:7px 18px;margin-bottom:20px;">
                        <div style="width:6px;height:6px;border-radius:50%;background:#818cf8;"></div>
                        <span style="font-size:10px;font-weight:800;letter-spacing:2.5px;text-transform:uppercase;color:#a5b4fc;">Official Follow-Up Documentation</span>
                    </div>
                    <h1 style="margin:0 0 8px 0;font-size:46px;font-weight:900;color:#ffffff;letter-spacing:-2px;line-height:1.05;">Interaction Record</h1>
                    <p style="margin:0;font-size:15px;color:#94a3b8;font-weight:400;">Single Follow-Up Entry · Verified System Document</p>
                </div>
                <div style="text-align:right;background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.1);border-radius:18px;padding:22px 30px;flex-shrink:0;">
                    <p style="margin:0 0 5px 0;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:2.5px;color:#64748b;">Document Date</p>
                    <p style="margin:0;font-size:15px;font-weight:800;color:#e2e8f0;font-family:'DM Mono',monospace;">${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                </div>
            </div>
        </div>

        <!-- CONTENT -->
        <div style="padding:44px 56px 52px;">

            <!-- STATUS BANNER -->
            <div style="margin-bottom:28px;display:flex;align-items:center;justify-content:space-between;background:#ffffff;border-radius:16px;padding:18px 28px;border:1px solid #e2e8f0;box-shadow:0 2px 12px rgba(0,0,0,0.05);">
                <div style="display:flex;align-items:center;gap:14px;">
                    <div style="width:42px;height:42px;border-radius:50%;background:${avatarBg};display:flex;align-items:center;justify-content:center;color:white;font-size:14px;font-weight:800;flex-shrink:0;box-shadow:0 4px 12px rgba(0,0,0,0.15);">${initials}</div>
                    <div>
                        <p style="margin:0 0 2px 0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;">Logged By</p>
                        <p style="margin:0;font-size:16px;font-weight:800;color:#0f172a;">${adminName}</p>
                    </div>
                </div>
                <div style="display:flex;align-items:center;gap:20px;">
                    <div style="text-align:right;">
                        <p style="margin:0 0 2px 0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;">Follow-Up Date</p>
                        <p style="margin:0;font-size:14px;font-weight:700;color:#1e293b;font-family:'DM Mono',monospace;">${followupDate}</p>
                    </div>
                    <span style="${statusStyle} font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:1px;padding:8px 20px;border-radius:100px;display:inline-block;flex-shrink:0;">${statusLabel}</span>
                </div>
            </div>

            <!-- TWO COLUMN: Customer + Session -->
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:28px;">

                <!-- Customer Card -->
                <div style="background:#ffffff;border-radius:20px;border:1px solid #e2e8f0;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.05);">
                    <div style="background:linear-gradient(90deg,#6366f1 0%,#8b5cf6 100%);padding:15px 24px;display:flex;align-items:center;gap:10px;">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        <span style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:2.5px;color:#ffffff;">Customer Profile</span>
                    </div>
                    <div style="padding:28px;">
                        <div style="margin-bottom:22px;">
                            <p style="margin:0 0 6px 0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;">Full Name</p>
                            <p style="margin:0;font-size:22px;font-weight:800;color:#0f172a;letter-spacing:-0.4px;">${customerName}</p>
                        </div>
                        <div>
                            <p style="margin:0 0 6px 0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;">Contact</p>
                            <div style="display:flex;align-items:center;gap:10px;">
                                <div style="width:32px;height:32px;border-radius:8px;background:#eff6ff;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                </div>
                                <p style="margin:0;font-size:16px;font-weight:700;color:#1e293b;font-family:'DM Mono',monospace;letter-spacing:0.3px;">${contactDetails}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Session Card -->
                <div style="background:#ffffff;border-radius:20px;border:1px solid #e2e8f0;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.05);">
                    <div style="background:linear-gradient(90deg,#0891b2 0%,#0d9488 100%);padding:15px 24px;display:flex;align-items:center;gap:10px;">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        <span style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:2.5px;color:#ffffff;">Session Details</span>
                    </div>
                    <div style="padding:28px;">
                        <div style="margin-bottom:22px;">
                            <p style="margin:0 0 6px 0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;">Date & Time</p>
                            <p style="margin:0;font-size:16px;font-weight:700;color:#0f172a;font-family:'DM Mono',monospace;">${followupDate}</p>
                        </div>
                        <!-- Next follow-up due -->
                        <div style="background:linear-gradient(135deg,#f0fdf4 0%,#dcfce7 100%);border:1.5px solid #86efac;border-radius:14px;padding:16px 20px;display:flex;align-items:center;gap:14px;">
                            <div style="width:36px;height:36px;border-radius:10px;background:#ffffff;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 2px 6px rgba(0,0,0,0.06);">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            </div>
                            <div>
                                <p style="margin:0 0 3px 0;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:1.5px;color:#15803d;">Next Follow-Up Due</p>
                                <p style="margin:0;font-size:17px;font-weight:900;color:#14532d;">${nextDate}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- REMARKS SECTION -->
            <div style="margin-bottom:28px;">
                <div style="display:flex;align-items:center;gap:14px;margin-bottom:14px;">
                    <div style="width:4px;height:24px;border-radius:3px;background:linear-gradient(180deg,#ec4899,#f59e0b);flex-shrink:0;"></div>
                    <span style="font-size:16px;font-weight:800;color:#0f172a;letter-spacing:-0.3px;">Interaction Notes & Commitments</span>
                    <div style="flex:1;height:1.5px;background:linear-gradient(90deg,#e2e8f0,transparent);"></div>
                </div>

                <div style="background:#ffffff;border-radius:18px;border:1px solid #e2e8f0;padding:32px 36px;box-shadow:0 4px 20px rgba(0,0,0,0.05);position:relative;overflow:hidden;">
                    <!-- Big decorative quote -->
                    <div style="position:absolute;top:16px;right:20px;font-size:120px;line-height:1;color:#f1f5f9;font-family:Georgia,serif;font-weight:900;user-select:none;">"</div>
                    <p style="position:relative;z-index:2;margin:0;font-size:16px;line-height:1.85;color:#334155;font-weight:500;white-space:pre-wrap;">${item.remarks || '<em style="color:#94a3b8;">No interaction remarks were documented for this session.</em>'}</p>
                </div>
            </div>

            <!-- FOOTER: Record ID + Signature -->
            <div style="background:#ffffff;border-radius:18px;border:1px solid #e2e8f0;padding:26px 32px;box-shadow:0 2px 10px rgba(0,0,0,0.04);display:flex;justify-content:space-between;align-items:flex-end;">
                <div>
                    <div style="display:flex;align-items:center;gap:10px;margin-bottom:16px;">
                        <div style="width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,#10b981,#059669);display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 4px 10px rgba(16,185,129,0.3);">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        </div>
                        <span style="font-size:13px;font-weight:800;color:#0f172a;">Verified System Record</span>
                    </div>
                    <p style="margin:0 0 6px 0;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:#94a3b8;">Record Reference ID</p>
                    <p style="margin:0;font-size:12px;font-weight:600;color:#475569;font-family:'DM Mono',monospace;background:#f8fafc;border:1px solid #e2e8f0;padding:7px 14px;border-radius:8px;display:inline-block;">${item._id}</p>
                </div>

                <div style="text-align:center;">
                    <div style="width:220px;border-bottom:2px dashed #cbd5e1;margin-bottom:14px;height:44px;"></div>
                    <p style="margin:0 0 4px 0;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:#94a3b8;">Authorized Signature</p>
                    <p style="margin:0;font-size:15px;font-weight:800;color:#0f172a;">${adminName}</p>
                </div>
            </div>

        </div>

        <!-- BOTTOM RAINBOW BAR -->
        <div style="height:5px;background:linear-gradient(90deg,#f59e0b 0%,#ec4899 35%,#8b5cf6 65%,#6366f1 100%);"></div>

    </div>`;

        printJS({
            printable: printContent,
            type: 'raw-html',
            style: `
            @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@400;500;600&display=swap');
            @page {
                size: A4 portrait;
                margin: 0mm;
            }
            html, body {
                margin: 0 !important;
                padding: 0 !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
                background: #f1f5f9;
            }
            * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
                box-sizing: border-box;
            }
        `,
            documentTitle: 'Follow-Up Interaction Record'
        });
    };

    const handlePrintFullHistory = () => {
        if (!sortedFollowUps || sortedFollowUps.length === 0) {
            alert("No follow-up history available to print.");
            return;
        }

        const customer = users?.find(u => u._id === customerId);
        const customerName = customer?.customerData?.name || 'Unknown Customer';
        const contactInfo = customer?.customerData?.contactInfo?.phone || 'N/A';
        const plotDisp = plotId?.plot_no ? `Plot #${plotId.plot_no}` : (plotId?.plot_number ? `Plot #${plotId.plot_number}` : 'N/A');

        const totalEntries = sortedFollowUps.length;
        const completed = sortedFollowUps.filter(f => f.status === 'Completed').length;
        const pending = sortedFollowUps.filter(f => !f.status || f.status === 'Pending').length;
        const cancelled = sortedFollowUps.filter(f => f.status === 'Cancelled').length;

        let rowsHtml = '';
        sortedFollowUps.forEach((item, index) => {

            // Priority: item.created_by.username → lookup by ID in users array → currentUser.username
            let resolvedAdminName = 'Unknown';
            if (item.created_by?.username || item.created_by?.name) {
                resolvedAdminName = item.created_by.username || item.created_by.name;
            } else {
                const createdById = typeof item.created_by === 'object' ? item.created_by?._id : item.created_by;
                const foundUser = users?.find(u => u._id === createdById);
                if (foundUser?.username || foundUser?.name) {
                    resolvedAdminName = foundUser.username || foundUser.name;
                } else if (foundUser?.customerData?.name) {
                    resolvedAdminName = foundUser.customerData.name;
                } else if (currentUser?.username || currentUser?.name) {
                    resolvedAdminName = currentUser.username || currentUser.name;
                }
            }

            const fDate = new Date(item.date).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
            const nDate = item.next_followup_date ? new Date(item.next_followup_date).toLocaleDateString() : '—';

            let statusStyle = '';
            let statusLabel = item.status || 'Pending';
            if (statusLabel === 'Completed') {
                statusStyle = 'background:#dcfce7; color:#15803d; border:1.5px solid #86efac;';
            } else if (statusLabel === 'Cancelled') {
                statusStyle = 'background:#fee2e2; color:#dc2626; border:1.5px solid #fca5a5;';
            } else {
                statusStyle = 'background:#fef9c3; color:#a16207; border:1.5px solid #fde047;';
            }

            const initials = resolvedAdminName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
            const avatarColors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];
            const avatarBg = avatarColors[resolvedAdminName.charCodeAt(0) % avatarColors.length];
            const rowBg = index % 2 === 0 ? '#ffffff' : '#fafbff';

            rowsHtml += `
        <tr style="background:${rowBg};">
            <td style="padding:16px 18px;border-bottom:1px solid #f1f5f9;vertical-align:top;">
                <span style="font-size:11.5px;color:#64748b;font-family:'DM Mono',monospace;white-space:nowrap;display:block;line-height:1.5;">${fDate}</span>
            </td>
            <td style="padding:16px 18px;border-bottom:1px solid #f1f5f9;vertical-align:top;">
                <div style="display:flex;align-items:center;gap:10px;white-space:nowrap;">
                    <div style="width:32px;height:32px;border-radius:50%;background:${avatarBg};color:white;font-size:11px;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0;">${initials}</div>
                    <span style="font-size:13px;font-weight:700;color:#1e293b;">${resolvedAdminName}</span>
                </div>
            </td>
            <td style="padding:16px 18px;border-bottom:1px solid #f1f5f9;vertical-align:top;max-width:300px;">
                <p style="margin:0;font-size:13px;color:#334155;line-height:1.7;">${item.remarks || '<em style="color:#94a3b8;font-size:12px;">No remarks provided</em>'}</p>
            </td>
            <td style="padding:16px 18px;border-bottom:1px solid #f1f5f9;vertical-align:top;text-align:center;">
                <span style="font-size:12px;font-weight:700;color:#475569;font-family:'DM Mono',monospace;white-space:nowrap;">${nDate}</span>
            </td>
            <td style="padding:16px 18px;border-bottom:1px solid #f1f5f9;vertical-align:top;text-align:center;">
                <span style="${statusStyle} font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.8px;padding:5px 14px;border-radius:100px;display:inline-block;">${statusLabel}</span>
            </td>
        </tr>`;
        });

        const statsCards = [
            { label: 'Total Entries', value: totalEntries, bg: '#eef2ff', text: '#4338ca', border: '#c7d2fe' },
            { label: 'Completed', value: completed, bg: '#f0fdf4', text: '#15803d', border: '#86efac' },
            { label: 'Pending', value: pending, bg: '#fffbeb', text: '#92400e', border: '#fde68a' },
            { label: 'Cancelled', value: cancelled, bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' },
        ].map(s => `
        <div style="background:${s.bg};border:1.5px solid ${s.border};border-radius:16px;padding:22px 20px;text-align:center;">
            <p style="margin:0 0 5px 0;font-size:40px;font-weight:900;color:${s.text};letter-spacing:-2px;line-height:1;">${s.value}</p>
            <p style="margin:0;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:1.5px;color:${s.text};opacity:0.75;">${s.label}</p>
        </div>`).join('');

        const printContent = `
    <div style="font-family:'Plus Jakarta Sans',sans-serif;background:#f1f5f9;color:#0f172a;margin:0;padding:0;">

        <!-- TOP RAINBOW BAR -->
        <div style="height:6px;background:linear-gradient(90deg,#6366f1 0%,#8b5cf6 30%,#ec4899 60%,#f59e0b 100%);"></div>

        <!-- DARK HEADER -->
        <div style="background:#0f172a;padding:48px 56px 44px;position:relative;overflow:hidden;">
            <div style="position:absolute;top:-70px;right:-70px;width:260px;height:260px;border-radius:50%;background:radial-gradient(circle,rgba(99,102,241,0.22) 0%,transparent 70%);"></div>
            <div style="position:absolute;bottom:-50px;left:160px;width:180px;height:180px;border-radius:50%;background:radial-gradient(circle,rgba(236,72,153,0.16) 0%,transparent 70%);"></div>
            <div style="position:absolute;top:20px;right:260px;width:100px;height:100px;border-radius:50%;background:radial-gradient(circle,rgba(251,191,36,0.13) 0%,transparent 70%);"></div>

            <div style="position:relative;z-index:2;display:flex;justify-content:space-between;align-items:center;">
                <div>
                    <div style="display:inline-flex;align-items:center;gap:8px;background:rgba(99,102,241,0.18);border:1px solid rgba(99,102,241,0.38);border-radius:100px;padding:7px 18px;margin-bottom:20px;">
                        <div style="width:6px;height:6px;border-radius:50%;background:#818cf8;"></div>
                        <span style="font-size:10px;font-weight:800;letter-spacing:2.5px;text-transform:uppercase;color:#a5b4fc;">Customer Interaction Report</span>
                    </div>
                    <h1 style="margin:0 0 8px 0;font-size:46px;font-weight:900;color:#ffffff;letter-spacing:-2px;line-height:1.05;">Follow-Up Report</h1>
                    <p style="margin:0;font-size:15px;color:#94a3b8;font-weight:400;">Comprehensive Interaction History Log</p>
                </div>
                <div style="text-align:right;background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.1);border-radius:18px;padding:22px 30px;flex-shrink:0;">
                    <p style="margin:0 0 5px 0;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:2.5px;color:#64748b;">Generated On</p>
                    <p style="margin:0;font-size:13px;font-weight:700;color:#e2e8f0;font-family:'DM Mono',monospace;">${new Date().toLocaleString()}</p>
                </div>
            </div>
        </div>

        <!-- CONTENT AREA -->
        <div style="padding:44px 56px 52px;">

            <!-- ACCOUNT CARD -->
            <div style="background:#ffffff;border-radius:20px;border:1px solid #e2e8f0;overflow:hidden;margin-bottom:28px;box-shadow:0 4px 20px rgba(0,0,0,0.06);">
                <div style="background:linear-gradient(90deg,#6366f1 0%,#8b5cf6 100%);padding:15px 28px;display:flex;align-items:center;gap:10px;">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    <span style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:2.5px;color:#ffffff;">Account Overview</span>
                </div>
                <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:0;">
                    <div style="padding:28px 30px;border-right:1px solid #f1f5f9;">
                        <p style="margin:0 0 7px 0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;">Customer Name</p>
                        <p style="margin:0;font-size:21px;font-weight:800;color:#0f172a;letter-spacing:-0.4px;">${customerName}</p>
                    </div>
                    <div style="padding:28px 30px;border-right:1px solid #f1f5f9;">
                        <p style="margin:0 0 7px 0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;">Contact Number</p>
                        <p style="margin:0;font-size:18px;font-weight:700;color:#1e293b;font-family:'DM Mono',monospace;letter-spacing:0.5px;">${contactInfo}</p>
                    </div>
                    <div style="padding:28px 30px;background:linear-gradient(135deg,#f0f9ff 0%,#e0f2fe 100%);">
                        <p style="margin:0 0 7px 0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#0284c7;">Property Reference</p>
                        <p style="margin:0;font-size:21px;font-weight:900;color:#0369a1;letter-spacing:-0.4px;">${plotDisp}</p>
                    </div>
                </div>
            </div>

            <!-- STATS ROW -->
            <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:32px;">
                ${statsCards}
            </div>

            <!-- TABLE SECTION HEADER -->
            <div style="display:flex;align-items:center;gap:14px;margin-bottom:14px;">
                <div style="width:4px;height:24px;border-radius:3px;background:linear-gradient(180deg,#6366f1,#ec4899);flex-shrink:0;"></div>
                <span style="font-size:16px;font-weight:800;color:#0f172a;letter-spacing:-0.3px;">Detailed History</span>
                <div style="flex:1;height:1.5px;background:linear-gradient(90deg,#e2e8f0,transparent);"></div>
            </div>

            <!-- TABLE -->
            <div style="background:#ffffff;border-radius:18px;border:1px solid #e2e8f0;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.05);">
                <table style="width:100%;border-collapse:collapse;text-align:left;">
                    <thead>
                        <tr style="background:linear-gradient(90deg,#1e293b 0%,#0f172a 100%);">
                            <th style="padding:16px 18px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;white-space:nowrap;width:130px;">Date & Time</th>
                            <th style="padding:16px 18px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;width:150px;">Representative</th>
                            <th style="padding:16px 18px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;">Remarks / Notes</th>
                            <th style="padding:16px 18px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;text-align:center;white-space:nowrap;width:100px;">Next Due</th>
                            <th style="padding:16px 18px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;text-align:center;width:110px;">Status</th>
                        </tr>
                    </thead>
                    <tbody>${rowsHtml}</tbody>
                </table>
            </div>

            <!-- FOOTER -->
            <div style="margin-top:32px;padding:22px 28px;background:#ffffff;border-radius:16px;border:1px solid #e2e8f0;display:flex;justify-content:space-between;align-items:center;box-shadow:0 2px 10px rgba(0,0,0,0.04);">
                <div style="display:flex;align-items:center;gap:14px;">
                    <div style="width:40px;height:40px;border-radius:50%;background:linear-gradient(135deg,#10b981,#059669);display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 4px 12px rgba(16,185,129,0.3);">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <div>
                        <p style="margin:0 0 3px 0;font-size:13px;font-weight:800;color:#0f172a;">End of verified system records.</p>
                        <p style="margin:0;font-size:11px;color:#94a3b8;font-family:'DM Mono',monospace;">Auto-generated · Structurally sealed · Do not alter</p>
                    </div>
                </div>
                <div style="text-align:right;">
                    <p style="margin:0 0 2px 0;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:#cbd5e1;">Total Records</p>
                    <p style="margin:0;font-size:34px;font-weight:900;color:#e2e8f0;letter-spacing:-1.5px;line-height:1;">${totalEntries}</p>
                </div>
            </div>

        </div>

        <!-- BOTTOM RAINBOW BAR -->
        <div style="height:5px;background:linear-gradient(90deg,#f59e0b 0%,#ec4899 35%,#8b5cf6 65%,#6366f1 100%);"></div>

    </div>`;

        printJS({
            printable: printContent,
            type: 'raw-html',
            style: `
            @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@400;500;600&display=swap');
            @page {
                size: A4 portrait;
                margin: 0mm;
            }
            html, body {
                margin: 0 !important;
                padding: 0 !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
                background: #f1f5f9;
            }
            * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
                box-sizing: border-box;
            }
            tr { page-break-inside: avoid; }
        `,
            documentTitle: 'Follow-Up Report'
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
            <div className={`${theme.tailwind.bg19} w-full max-w-5xl h-[85vh] rounded-2xl shadow-2xl border ${theme.tailwind.border17} flex overflow-hidden`}>

                {/* LEFT SIDE: Timeline History */}
                <div className={`flex-1 flex flex-col border-r ${theme.tailwind.border17} relative`}>
                    <div className={`p-5 border-b ${theme.tailwind.border17} flex items-center justify-between bg-black/5`}>
                        <div className="flex items-center gap-4">
                            <h2 className={`text-xl font-bold ${theme.tailwind.text9} flex items-center gap-3`}>
                                <Clock className={`w-6 h-6 ${theme.tailwind.text12}`} />
                                Follow-up History
                            </h2>
                            <span className={`text-sm ${theme.tailwind.text5} px-3 py-1 rounded-full ${theme.tailwind.bg3} border ${theme.tailwind.border2}`}>
                                {followUps.length} Records
                            </span>
                        </div>
                        {followUps.length > 0 && (
                            <button
                                onClick={handlePrintFullHistory}
                                className={`flex items-center gap-2 px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-400 rounded-lg text-sm font-semibold transition-all`}
                            >
                                <Printer className="w-4 h-4" />
                                Print Report
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                        {loading && followUps.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full gap-3 opacity-60">
                                <div className="w-8 h-8 border-4 border-current border-t-transparent rounded-full animate-spin" />
                                <p>Loading history...</p>
                            </div>
                        ) : followUps.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full gap-4 text-center opacity-50">
                                <Clock className="w-16 h-16 opacity-20" />
                                <p>No follow-ups recorded yet.<br />Start by adding a new remark.</p>
                            </div>
                        ) : (
                            <div className="space-y-0">
                                {sortedFollowUps.map((item, index) => (
                                    <div key={item._id} className="relative pl-8 pb-10 last:pb-0 group">
                                        {/* Timeline Connector */}
                                        {index !== sortedFollowUps.length - 1 && (
                                            <div className={`absolute left-[11px] top-8 bottom-0 w-[2px] ${theme.tailwind.bg17} group-hover:bg-blue-500/30 transition-colors`} />
                                        )}

                                        {/* Timeline Dot */}
                                        <div className={`absolute left-0 top-1 w-6 h-6 rounded-full border-2 ${theme.tailwind.border17} ${theme.tailwind.bg2} flex items-center justify-center z-10 group-hover:border-blue-500 transition-colors shadow-sm`}>
                                            <div className={`w-2 h-2 rounded-full ${theme.tailwind.bg12}`} />
                                        </div>

                                        {/* Content Card */}
                                        <div className={`rounded-xl border ${theme.tailwind.border17} ${theme.tailwind.bg2} hover:${theme.tailwind.bg3} transition-all duration-200 shadow-sm p-4 -mt-2 group-hover:shadow-md group-hover:translate-x-1`}>
                                            <div className="flex justify-between items-start mb-3 border-b border-gray-700/30 pb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-gray-700/50`}>
                                                        <User className={`w-4 h-4 ${theme.tailwind.text12}`} />
                                                    </div>
                                                    <div>
                                                        <span className={`block text-sm font-semibold ${theme.tailwind.text10} leading-tight`}>
                                                            {item.created_by?.name || 'Unknown User'}
                                                        </span>
                                                        <span className={`text-[10px] uppercase tracking-wider ${theme.tailwind.text12} opacity-70`}>
                                                            {item.created_by?.role || 'System'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-2">
                                                    <div className="flex items-center gap-3">
                                                        <span className={`text-xs ${theme.tailwind.text12} font-medium`}>
                                                            {new Date(item.date).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                                        </span>
                                                        {(currentUser?.role === 'Admin' || currentUser?._id === item.created_by?._id) && (
                                                            <button
                                                                onClick={() => handleDelete(item._id)}
                                                                className="text-red-400/60 hover:text-red-400 p-1.5 rounded-full hover:bg-red-400/10 transition-colors"
                                                                title="Delete"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handlePrintFollowUp(item)}
                                                            className="text-blue-400/60 hover:text-blue-400 p-1.5 rounded-full hover:bg-blue-400/10 transition-colors"
                                                            title="Print Record"
                                                        >
                                                            <Printer className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>

                                                    {/* Status Selector */}
                                                    <select
                                                        value={item.status || 'Pending'}
                                                        onChange={(e) => dispatch(updateFollowUpStatus({ id: item._id, status: e.target.value }))}
                                                        className={`text-[10px] font-bold px-2 py-1 rounded border outline-none cursor-pointer transition-all ${item.status === 'Completed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                                                            item.status === 'Cancelled' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                                                                'bg-amber-500/10 text-amber-400 border-amber-500/30'
                                                            }`}
                                                    >
                                                        <option value="Pending">Pending</option>
                                                        <option value="Completed">Completed</option>
                                                        <option value="Cancelled">Cancelled</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className={`pl-11`}>
                                                <p className={`${theme.tailwind.text9} whitespace-pre-wrap text-sm leading-relaxed mb-3`}>
                                                    {item.remarks}
                                                </p>

                                                <div className="flex flex-wrap gap-2">
                                                    {item.next_followup_date && (
                                                        <div className={`inline-flex items-center gap-2 text-xs font-medium ${theme.tailwind.text12} bg-blue-500/10 border border-blue-500/20 px-3 py-1.5 rounded-lg`}>
                                                            <Calendar className="w-3.5 h-3.5" />
                                                            <span>Next: {new Date(item.next_followup_date).toLocaleDateString()}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT SIDE: Add Form */}
                <div className={`w-[350px] md:w-[400px] flex flex-col border-l ${theme.tailwind.border17} ${theme.tailwind.bg2} relative`}>
                    <div className={`p-5 border-b ${theme.tailwind.border17} flex justify-between items-center bg-black/5`}>
                        <h3 className={`font-semibold ${theme.tailwind.text10} text-lg`}>Add Update</h3>
                        <button
                            onClick={onClose}
                            className={`p-2 rounded-full hover:bg-red-500/10 hover:text-red-500 ${theme.tailwind.text5} transition-all`}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="flex-1 flex flex-col p-6 space-y-5 overflow-y-auto">
                        <div className="flex-1 space-y-4">
                            <div>
                                <label className={`block text-xs font-semibold uppercase tracking-wider ${theme.tailwind.text12} mb-2 ml-1`}>
                                    Remarks / Notes
                                </label>
                                <textarea
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    placeholder="Type your discussion details, customer feedback, or internal notes here..."
                                    className={`w-full h-[calc(100%-2rem)] min-h-[200px] px-4 py-4 rounded-xl ${theme.tailwind.bg3} border ${theme.tailwind.border2} focus:${theme.tailwind.border6} focus:ring-2 focus:${theme.tailwind.ringColor2} outline-none ${theme.tailwind.text9} transition-all resize-none placeholder-gray-500/50 leading-relaxed`}
                                    autoFocus
                                    required
                                />
                            </div>

                            <div>
                                <label className={`block text-xs font-semibold uppercase tracking-wider ${theme.tailwind.text12} mb-2 ml-1`}>
                                    Next Follow-up Date (Optional)
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Calendar className={`h-5 w-5 ${theme.tailwind.text14}`} />
                                    </div>
                                    <input
                                        type="date"
                                        value={nextDate}
                                        onChange={(e) => setNextDate(e.target.value)}
                                        className={`w-full pl-10 pr-4 py-3 rounded-xl ${theme.tailwind.bg3} border ${theme.tailwind.border2} focus:${theme.tailwind.border6} focus:ring-2 focus:${theme.tailwind.ringColor2} outline-none ${theme.tailwind.text9} transition-all`}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={`block text-xs font-semibold uppercase tracking-wider ${theme.tailwind.text12} mb-2 ml-1`}>
                                    Status
                                </label>
                                <select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    className={`w-full px-4 py-3 rounded-xl ${theme.tailwind.bg3} border ${theme.tailwind.border2} focus:${theme.tailwind.border6} focus:ring-2 focus:${theme.tailwind.ringColor2} outline-none ${theme.tailwind.text9} transition-all cursor-pointer`}
                                >
                                    <option value="Pending">Pending</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Cancelled">Cancelled</option>
                                </select>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !remarks.trim()}
                            className={`w-full py-4 ${theme.tailwind.bg25} hover:${theme.tailwind.bgHover13} ${theme.tailwind.text9} rounded-xl font-bold text-sm tracking-wide transition-all uppercase flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0 ${loading || !remarks.trim() ? 'opacity-50 cursor-not-allowed transform-none shadow-none' : ''}`}
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <Plus className="w-5 h-5" />
                            )}
                            Post Update
                        </button>
                    </form>
                </div>

            </div>
        </div>
    );
};

export default FollowUpModal;
